import { emailRegex, validationTranslationKeys } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { SenderEmail } from '@va/shared/api/custom-domains';
import { SubSectionHeader } from '@va/ui/components/white-label-customisation';
import { ActionInput, Paragraph } from '@va/ui/design-system';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import { object, string } from 'yup';
import { useSendDummyEmail } from '../apiClient';

export const SendTestEmailSection = ({ activeSender }: { activeSender?: SenderEmail | null }) => {
  const translate = useTranslate();
  const { execute, isLoading } = useSendDummyEmail();
  const { showErrorNotification, showSuccessNotification } = useAddNotification();

  const { values, submitForm, errors, handleChange, setFieldValue, dirty } = useFormik({
    initialValues: {
      receivingAddress: '',
    },
    isInitialValid: true,
    onSubmit: async ({ receivingAddress }) => {
      try {
        await execute(receivingAddress, 'sent_by_dummy');
        showSuccessNotification(translate('customSenderEmail.sendTestEmail.successMessage'));
      } catch (error) {
        showErrorNotification();
      }
    },
    validationSchema: object({
      receivingAddress: string()
        .matches(emailRegex, translate(validationTranslationKeys.invalidEmail))
        .required(translate(validationTranslationKeys.required)),
    }),
  });

  const buttonTooltip = useMemo(() => {
    if (!dirty) return translate('customSenderEmail.sendTestEmail.buttonTooltip.noChanges');
    if (!activeSender) return translate('customSenderEmail.sendTestEmail.buttonTooltip.noActiveSender');
    return undefined;
  }, [activeSender, dirty, translate]);

  return (
    <div>
      <SubSectionHeader
        tooltip={<Paragraph>{translate('customSenderEmail.sendTestEmail.sectionTooltip')}</Paragraph>}
        text={translate('whiteLabel.email.sendDummy.title')}
      />
      <ActionInput
        info={translate('customSenderEmail.sendTestEmail.info')}
        buttonProps={{
          onClick: () => submitForm(),
          text: isLoading ? translate('button.loading') : translate('button.send'),
          tooltip: buttonTooltip,
          disabled: isLoading || !activeSender,
        }}
        textInputProps={{
          error: errors.receivingAddress,
          value: values.receivingAddress,
          name: 'receivingAddress',
          showErrorMessage: false,
          onChange: handleChange,
          clearField: () => setFieldValue('receivingAddress', ''),
          label: translate('customSenderEmail.sendTestEmail.inputLabel'),
          placeholder: '',
        }}
      />
    </div>
  );
};
